// src/ProgramAutocomplete.jsx
// Autocomplete catalogo programmi per CreateMaestranzaForm (solo inserimento nuovo).
// Al cambio di selezione chiama onChange({ program, edition, freeText })
//   - program:   { id, name, is_verified } | null  (null se testo libero)
//   - edition:   { id, edition_label, year } | null
//   - freeText:  string (valorizzato solo se l'utente sceglie "Usa testo libero")

import React, { useEffect, useState, useCallback } from 'react';
import { fetchJson } from './api';
import { useTranslation } from 'react-i18next';

// ─── Palette (identica a MieiCreditiPage / progetto JKL) ─────────────────────
const GOLD        = '#d4a550';
const GOLD_DIM    = 'rgba(212,165,80,0.13)';
const GOLD_BORDER = 'rgba(212,165,80,0.28)';
const BORDER      = 'rgba(255,255,255,0.07)';
const TEXT        = '#e8e6df';
const TEXT_DIM    = 'rgba(232,230,223,0.5)';
const BG2         = '#13131f';
const BG3         = '#1a1a28';
const GREEN       = '#4ade80';
const GREEN_DIM   = 'rgba(74,222,128,0.13)';

// ─── useDebounce (copiato da MieiCreditiPage per non introdurre dipendenza) ───
function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

// ─── Componente principale ────────────────────────────────────────────────────
export default function ProgramAutocomplete({ value, onChange, disabled }) {
  const { t } = useTranslation();

  // ── Stato ricerca programma ───────────────────────────────────────────────
  const [query, setQuery]             = useState(value || '');
  const [results, setResults]         = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // ── Programma selezionato ─────────────────────────────────────────────────
  const [selectedProgram, setSelectedProgram] = useState(null);

  // ── Edizioni ─────────────────────────────────────────────────────────────
  const [editions, setEditions]         = useState([]);
  const [editionsLoading, setEditionsLoading] = useState(false);
  const [editionSelectValue, setEditionSelectValue] = useState(''); // id come stringa, o 'new'

  // ── Nuova edizione inline ─────────────────────────────────────────────────
  const [showNewEdition, setShowNewEdition] = useState(false);
  const [newLabel, setNewLabel]     = useState('');
  const [newYear, setNewYear]       = useState('');
  const [savingEdition, setSavingEdition] = useState(false);

  // ── Modalità testo libero ─────────────────────────────────────────────────
  const [freeTextMode, setFreeTextMode] = useState(false);

  const debouncedQuery = useDebounce(query, 300);

  // ── Ricerca programmi (debounced) ─────────────────────────────────────────
  useEffect(() => {
    if (freeTextMode || selectedProgram) {
      setResults([]);
      return;
    }
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setResults([]);
      setDropdownOpen(false);
      return;
    }
    let cancelled = false;
    setSearchLoading(true);
    fetchJson(`/cv/programs?q=${encodeURIComponent(debouncedQuery)}`)
      .then(data => {
        if (!cancelled) {
          setResults(data?.programs || []);
          setDropdownOpen(true);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setSearchLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedQuery, freeTextMode, selectedProgram]);

  // ── Carica edizioni quando si seleziona un programma ─────────────────────
  const loadEditions = useCallback(async (prog) => {
    setEditionsLoading(true);
    setEditions([]);
    setEditionSelectValue('');
    setShowNewEdition(false);
    try {
      const data = await fetchJson(`/cv/programs/${prog.id}/editions`);
      setEditions(data?.editions || []);
    } catch {
      setEditions([]);
    } finally {
      setEditionsLoading(false);
    }
  }, []);

  // ── Selezione programma dal dropdown ──────────────────────────────────────
  const handleSelectProgram = useCallback((prog) => {
    setSelectedProgram(prog);
    setQuery(prog.name);
    setResults([]);
    setDropdownOpen(false);
    setFreeTextMode(false);
    loadEditions(prog);
    // Notifica parent: programma scelto, edizione ancora da selezionare
    onChange({ program: prog, edition: null, freeText: '' });
  }, [loadEditions, onChange]);

  // ── Scelta "testo libero" ─────────────────────────────────────────────────
  const handleFreeText = useCallback(() => {
    setFreeTextMode(true);
    setSelectedProgram(null);
    setEditions([]);
    setEditionSelectValue('');
    setDropdownOpen(false);
    setResults([]);
    onChange({ program: null, edition: null, freeText: query });
  }, [query, onChange]);

  // ── Reset selezione programma ─────────────────────────────────────────────
  const handleResetProgram = useCallback(() => {
    setSelectedProgram(null);
    setQuery('');
    setEditions([]);
    setEditionSelectValue('');
    setShowNewEdition(false);
    setFreeTextMode(false);
    onChange({ program: null, edition: null, freeText: '' });
  }, [onChange]);

  // ── Cambio <select> edizione ──────────────────────────────────────────────
  const handleEditionChange = useCallback((e) => {
    const val = e.target.value;
    setEditionSelectValue(val);
    if (val === 'new') {
      setShowNewEdition(true);
      onChange({ program: selectedProgram, edition: null, freeText: '' });
    } else if (val === '') {
      setShowNewEdition(false);
      onChange({ program: selectedProgram, edition: null, freeText: '' });
    } else {
      const ed = editions.find(x => String(x.id) === val);
      setShowNewEdition(false);
      onChange({ program: selectedProgram, edition: ed || null, freeText: '' });
    }
  }, [editions, selectedProgram, onChange]);

  // ── Salva nuova edizione ──────────────────────────────────────────────────
  const handleSaveEdition = useCallback(async () => {
    if (!newLabel.trim() || !selectedProgram) return;
    setSavingEdition(true);
    try {
      const data = await fetchJson('/cv/editions', {
        method: 'POST',
        body: JSON.stringify({
          program_id: selectedProgram.id,
          edition_label: newLabel.trim(),
          edition_no: parseInt(newLabel.trim(), 10) || null,
          year: newYear ? parseInt(newYear, 10) : null,
        }),
      });
      const newEd = data?.edition || data;
      setEditions(prev => [...prev, newEd]);
      setEditionSelectValue(String(newEd.id));
      setShowNewEdition(false);
      setNewLabel('');
      setNewYear('');
      onChange({ program: selectedProgram, edition: newEd, freeText: '' });
    } catch {
      // ignora errore silenziosamente (l'edizione non viene aggiunta)
    } finally {
      setSavingEdition(false);
    }
  }, [newLabel, newYear, selectedProgram, onChange]);

  // ── Aggiorna testo libero quando query cambia ─────────────────────────────
  const handleQueryChange = useCallback((e) => {
    const val = e.target.value;
    setQuery(val);
    if (freeTextMode) {
      onChange({ program: null, edition: null, freeText: val });
    }
  }, [freeTextMode, onChange]);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>

      {/* Input ricerca / programma selezionato */}
      {!selectedProgram && (
        <div style={{ position: 'relative' }}>
          <input
            type="text"
            value={query}
            onChange={handleQueryChange}
            onFocus={() => { if (results.length > 0) setDropdownOpen(true); }}
            onBlur={() => setTimeout(() => setDropdownOpen(false), 160)}
            placeholder={t('produzione.autocomplete_placeholder')}
            disabled={disabled}
            style={{
              width: '100%', boxSizing: 'border-box',
              background: BG2,
              border: `1px solid ${freeTextMode ? GOLD_BORDER : BORDER}`,
              borderRadius: 8, padding: '10px 36px 10px 12px',
              color: TEXT, fontSize: 13, outline: 'none',
              fontFamily: 'inherit',
              opacity: disabled ? 0.5 : 1,
            }}
          />
          {/* Spinner */}
          {searchLoading && (
            <span style={{
              position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)', color: TEXT_DIM, fontSize: 11,
            }}>
              ...
            </span>
          )}
          {/* Badge testo libero attivo */}
          {freeTextMode && (
            <span style={{
              position: 'absolute', right: 10, top: '50%',
              transform: 'translateY(-50%)',
              fontSize: 9, fontWeight: 700, padding: '2px 6px', borderRadius: 10,
              background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD,
            }}>
              {t('produzione.testo_libero_badge')}
            </span>
          )}

          {/* Dropdown risultati */}
          {dropdownOpen && (results.length > 0 || query.length >= 2) && !freeTextMode && (
            <div style={{
              position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
              background: BG2, border: `1px solid ${BORDER}`, borderRadius: 8,
              marginTop: 2, maxHeight: 240, overflowY: 'auto',
              boxShadow: '0 8px 24px rgba(0,0,0,0.55)',
            }}>
              {/* Voci catalogo */}
              {results.map(p => (
                <div
                  key={p.id}
                  onMouseDown={() => handleSelectProgram(p)}
                  style={{
                    padding: '9px 14px', cursor: 'pointer', fontSize: 13, color: TEXT,
                    display: 'flex', alignItems: 'center', gap: 8,
                    borderBottom: `1px solid ${BORDER}`,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = BG3}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <span style={{ flex: 1 }}>{p.name}</span>
                  {p.is_verified && (
                    <span style={{
                      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
                      background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD,
                      whiteSpace: 'nowrap',
                    }}>
                      {t('produzione.verificato')}
                    </span>
                  )}
                </div>
              ))}

              {/* Opzione testo libero (sempre in fondo) */}
              {query.length >= 2 && (
                <div
                  onMouseDown={handleFreeText}
                  style={{
                    padding: '9px 14px', cursor: 'pointer', fontSize: 12,
                    color: TEXT_DIM, fontStyle: 'italic',
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}
                  onMouseEnter={e => e.currentTarget.style.background = BG3}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {t('produzione.usa_testo_libero', { testo: query })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Chip programma selezionato */}
      {selectedProgram && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`,
          borderRadius: 8, padding: '8px 12px',
        }}>
          <span style={{ color: GOLD, fontSize: 13, fontWeight: 600, flex: 1 }}>
            {selectedProgram.name}
          </span>
          {selectedProgram.is_verified && (
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20,
              background: GOLD_DIM, border: `1px solid ${GOLD_BORDER}`, color: GOLD,
            }}>
              {t('produzione.verificato')}
            </span>
          )}
          <button
            type="button"
            onClick={handleResetProgram}
            style={{
              background: 'none', border: 'none', color: TEXT_DIM,
              cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0,
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* Selettore edizione (appare solo dopo selezione programma da catalogo) */}
      {selectedProgram && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {editionsLoading ? (
            <span style={{ fontSize: 12, color: TEXT_DIM }}>
              {t('crediti.loading_edizioni')}
            </span>
          ) : (
            <select
              value={editionSelectValue}
              onChange={handleEditionChange}
              disabled={disabled}
              style={{
                width: '100%', background: BG2,
                border: `1px solid ${BORDER}`, borderRadius: 8,
                padding: '9px 12px',
                color: editionSelectValue ? TEXT : TEXT_DIM,
                fontSize: 13, outline: 'none', cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              <option value="">{t('produzione.seleziona_edizione')}</option>
              {editions.map(ed => (
                <option key={ed.id} value={String(ed.id)}>
                  {ed.edition_label
                    ? `${t('crediti.edizione_prefix')} ${ed.edition_label}${ed.year ? ` (${ed.year})` : ''}`
                    : ed.edition_no
                      ? `${t('crediti.edizione_prefix')} ${ed.edition_no}${ed.year ? ` (${ed.year})` : ''}`
                      : `ID ${ed.id}`}
                </option>
              ))}
              <option value="new">{t('produzione.aggiungi_edizione')}</option>
            </select>
          )}

          {/* Form inline nuova edizione */}
          {showNewEdition && (
            <div style={{
              background: BG3, border: `1px solid ${BORDER}`,
              borderRadius: 10, padding: '14px 16px',
              display: 'flex', flexDirection: 'column', gap: 10,
            }}>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 120 }}>
                  <label style={{
                    display: 'block', fontSize: 10, color: TEXT_DIM,
                    fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.07em', marginBottom: 4,
                  }}>
                    {t('produzione.nuova_edizione_label')}
                  </label>
                  <input
                    type="text"
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    placeholder={t('crediti.ph_edition_label')}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      background: BG2, border: `1px solid ${BORDER}`,
                      borderRadius: 6, padding: '7px 10px',
                      color: TEXT, fontSize: 13, outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 100 }}>
                  <label style={{
                    display: 'block', fontSize: 10, color: TEXT_DIM,
                    fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: '0.07em', marginBottom: 4,
                  }}>
                    {t('produzione.nuova_edizione_anno')}
                  </label>
                  <input
                    type="number"
                    value={newYear}
                    onChange={e => setNewYear(e.target.value)}
                    placeholder={t('crediti.ph_year')}
                    min={1990} max={2100}
                    style={{
                      width: '100%', boxSizing: 'border-box',
                      background: BG2, border: `1px solid ${BORDER}`,
                      borderRadius: 6, padding: '7px 10px',
                      color: TEXT, fontSize: 13, outline: 'none',
                      fontFamily: 'inherit',
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={handleSaveEdition}
                  disabled={savingEdition || !newLabel.trim()}
                  style={{
                    padding: '7px 16px', borderRadius: 8, fontSize: 12, fontWeight: 700,
                    background: savingEdition || !newLabel.trim()
                      ? 'rgba(255,255,255,0.05)' : GREEN_DIM,
                    border: `1px solid ${GREEN}`, color: GREEN,
                    cursor: savingEdition || !newLabel.trim() ? 'not-allowed' : 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {savingEdition ? t('crediti.saving') : t('crediti.salva_edizione_btn')}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowNewEdition(false);
                    setEditionSelectValue('');
                    setNewLabel('');
                    setNewYear('');
                  }}
                  style={{
                    padding: '7px 14px', borderRadius: 8, fontSize: 12,
                    background: 'none', border: `1px solid ${BORDER}`,
                    color: TEXT_DIM, cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {t('crediti.annulla')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
